import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import API_BASE_URL from '../config/apiConfig';

const TOKEN_STORAGE_KEY = 'sk_auth_token';
const USER_STORAGE_KEY = 'sk_auth_user';
const CART_STORAGE_KEY = 'sk_cart_items';

const readAuthStorage = () => {
  if (typeof window === 'undefined') {
    return { token: '', user: null };
  }

  try {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY) || '';
    const userRaw = window.localStorage.getItem(USER_STORAGE_KEY);
    const user = userRaw ? JSON.parse(userRaw) : null;

    return {
      token,
      user: user && typeof user === 'object' ? user : null,
    };
  } catch (_) {
    return { token: '', user: null };
  }
};

const writeAuthStorage = (token, user) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (token) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }

    if (user) {
      window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(USER_STORAGE_KEY);
    }
  } catch (_) {
    // ignore localStorage errors
  }
};

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [{ token, user }, setAuthState] = useState(() => readAuthStorage());

  useEffect(() => {
    writeAuthStorage(token, user);
  }, [token, user]);

  const setSession = useCallback((sessionToken, sessionUser) => {
    setAuthState({
      token: sessionToken,
      user: sessionUser,
    });
  }, []);

  const updateAuthUser = useCallback((sessionUser) => {
    setAuthState((previous) => ({
      token: previous.token,
      user: sessionUser,
    }));
  }, []);

  const login = useCallback(async ({ email, identifier, password }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: identifier || email, password }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        return {
          success: false,
          status: response.status,
          code: result?.code || 'LOGIN_FAILED',
          message: result?.message || 'Unable to login.',
        };
      }

      setAuthState({
        token: result.token,
        user: result.user,
      });

      return { success: true, data: result };
    } catch (_) {
      return {
        success: false,
        status: 500,
        code: 'NETWORK_ERROR',
        message: 'Unable to reach server. Please try again.',
      };
    }
  }, []);

  const register = useCallback(async ({ userName, email, phone, password }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userName, email, phone, password }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        return {
          success: false,
          status: response.status,
          code: result?.code || 'REGISTER_FAILED',
          message: result?.message || 'Unable to register.',
        };
      }

      return { success: true, data: result };
    } catch (_) {
      return {
        success: false,
        status: 500,
        code: 'NETWORK_ERROR',
        message: 'Unable to reach server. Please try again.',
      };
    }
  }, []);

  const resendOtp = useCallback(async ({ email }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        return {
          success: false,
          status: response.status,
          code: result?.code || 'SEND_OTP_FAILED',
          message: result?.message || 'Unable to send OTP.',
        };
      }

      return { success: true, data: result };
    } catch (_) {
      return {
        success: false,
        status: 500,
        code: 'NETWORK_ERROR',
        message: 'Unable to reach server. Please try again.',
      };
    }
  }, []);

  const verifyOtp = useCallback(async ({ email, otp }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        return {
          success: false,
          status: response.status,
          code: result?.code || 'VERIFY_OTP_FAILED',
          message: result?.message || 'Unable to verify OTP.',
        };
      }

      setAuthState({
        token: result.token,
        user: result.user,
      });

      return { success: true, data: result };
    } catch (_) {
      return {
        success: false,
        status: 500,
        code: 'NETWORK_ERROR',
        message: 'Unable to reach server. Please try again.',
      };
    }
  }, []);

  const forgotPassword = useCallback(async ({ email }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        return {
          success: false,
          status: response.status,
          code: result?.code || 'FORGOT_PASSWORD_FAILED',
          message: result?.message || 'Unable to process forgot password request.',
        };
      }

      return { success: true, data: result };
    } catch (_) {
      return {
        success: false,
        status: 500,
        code: 'NETWORK_ERROR',
        message: 'Unable to reach server. Please try again.',
      };
    }
  }, []);

  const resetPassword = useCallback(async ({ token, newPassword }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        return {
          success: false,
          status: response.status,
          code: result?.code || 'RESET_PASSWORD_FAILED',
          message: result?.message || 'Unable to reset password.',
        };
      }

      return { success: true, data: result };
    } catch (_) {
      return {
        success: false,
        status: 500,
        code: 'NETWORK_ERROR',
        message: 'Unable to reach server. Please try again.',
      };
    }
  }, []);

  const requestLoginOtp = useCallback(async ({ identifier }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/login-otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        return { success: false, status: response.status, code: result?.code, message: result?.message || 'Unable to send OTP.' };
      }
      return { success: true, data: result };
    } catch (_) {
      return { success: false, code: 'NETWORK_ERROR', message: 'Unable to reach server. Please try again.' };
    }
  }, []);

  const verifyLoginOtp = useCallback(async ({ identifier, otp }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/login-otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, otp }),
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        return { success: false, status: response.status, code: result?.code, message: result?.message || 'Unable to verify OTP.' };
      }
      setAuthState({ token: result.token, user: result.user });
      return { success: true, data: result };
    } catch (_) {
      return { success: false, code: 'NETWORK_ERROR', message: 'Unable to reach server. Please try again.' };
    }
  }, []);

  const forgotPasswordMobileRequestOtp = useCallback(async ({ phone }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/forgot-password/mobile/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        return { success: false, message: result?.message || 'Unable to send OTP.' };
      }
      return { success: true, data: result };
    } catch (_) {
      return { success: false, message: 'Unable to reach server.' };
    }
  }, []);

  const forgotPasswordMobileConfirm = useCallback(async ({ phone, otp, newPassword }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ec/auth/forgot-password/mobile/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp, newPassword }),
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        return { success: false, message: result?.message || 'Unable to reset password.' };
      }
      return { success: true, data: result };
    } catch (_) {
      return { success: false, message: 'Unable to reach server.' };
    }
  }, []);

  const authFetch = useCallback(async (path, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
    const result = await response.json();
    return { response, result };
  }, [token]);

  const refreshProfile = useCallback(async () => {
    const { response, result } = await authFetch('/ec/auth/profile');
    if (!response.ok || !result?.success || !result.data) {
      return { success: false, message: result?.message || 'Unable to load profile.' };
    }
    const profile = result.data;
    updateAuthUser({
      UserId: profile.UserId,
      UserName: profile.UserName,
      Email: profile.Email,
      Phone: profile.Phone,
      IsEmailVerified: Number(profile.IsEmailVerified) === 1 ? 1 : 0,
      IsPhoneVerified: Number(profile.IsPhoneVerified) === 1 ? 1 : 0,
      IsProfileComplete: Number(profile.IsProfileComplete) === 1 ? 1 : 0,
      CreatedAt: profile.CreatedAt,
    });
    return { success: true, data: profile };
  }, [authFetch, updateAuthUser]);

  const updateProfile = useCallback(async (body) => {
    const { response, result } = await authFetch('/ec/auth/profile', { method: 'POST', body: JSON.stringify(body) });
    if (!response.ok || !result?.success) {
      return { success: false, message: result?.message || 'Unable to update profile.' };
    }
    if (result.user) {
      updateAuthUser(result.user);
    }
    return { success: true, data: result };
  }, [authFetch, updateAuthUser]);

  const profilePhoneRequestOtp = useCallback(async ({ phone }) => {
    const { response, result } = await authFetch('/ec/auth/profile/phone/request-otp', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
    if (!response.ok || !result?.success) {
      return { success: false, message: result?.message || 'Unable to send OTP.' };
    }
    return { success: true, data: result };
  }, [authFetch]);

  const profilePhoneVerifyOtp = useCallback(async ({ phone, otp }) => {
    const { response, result } = await authFetch('/ec/auth/profile/phone/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ phone, otp }),
    });
    if (!response.ok || !result?.success) {
      return { success: false, message: result?.message || 'Unable to verify phone.' };
    }
    if (result.user) {
      updateAuthUser(result.user);
    }
    return { success: true, data: result };
  }, [authFetch, updateAuthUser]);

  const profileEmailRequestOtp = useCallback(async ({ email }) => {
    const { response, result } = await authFetch('/ec/auth/profile/email/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    if (!response.ok || !result?.success) {
      return { success: false, message: result?.message || 'Unable to send OTP.' };
    }
    return { success: true, data: result };
  }, [authFetch]);

  const profileEmailVerifyOtp = useCallback(async ({ email, otp }) => {
    const { response, result } = await authFetch('/ec/auth/profile/email/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
    if (!response.ok || !result?.success) {
      return { success: false, message: result?.message || 'Unable to verify email.' };
    }
    if (result.user) {
      updateAuthUser(result.user);
    }
    return { success: true, data: result };
  }, [authFetch, updateAuthUser]);

  const logout = useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(CART_STORAGE_KEY);
      } catch (_) {
        // ignore localStorage errors
      }
    }

    setAuthState({ token: '', user: null });
  }, []);

  const isAuthenticated = useMemo(() => Boolean(token && user && user.UserId), [token, user]);

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated,
      login,
      register,
      resendOtp,
      verifyOtp,
      forgotPassword,
      resetPassword,
      requestLoginOtp,
      verifyLoginOtp,
      forgotPasswordMobileRequestOtp,
      forgotPasswordMobileConfirm,
      refreshProfile,
      updateProfile,
      profilePhoneRequestOtp,
      profilePhoneVerifyOtp,
      profileEmailRequestOtp,
      profileEmailVerifyOtp,
      updateAuthUser,
      setSession,
      logout,
    }),
    [
      token,
      user,
      isAuthenticated,
      login,
      register,
      resendOtp,
      verifyOtp,
      forgotPassword,
      resetPassword,
      requestLoginOtp,
      verifyLoginOtp,
      forgotPasswordMobileRequestOtp,
      forgotPasswordMobileConfirm,
      refreshProfile,
      updateProfile,
      profilePhoneRequestOtp,
      profilePhoneVerifyOtp,
      profileEmailRequestOtp,
      profileEmailVerifyOtp,
      updateAuthUser,
      setSession,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};

export default AuthContext;
